/**
 * KB-META
 * Purpose: App menuitem
 * Role: ui.component
 * EntryPoints: constructor(), if(), updateActiveStateFromRoute(), toggleSection()
 * Inputs: active:unknown; public router:Router; public layoutService:LayoutService; private helpService:MenuHelpService; this.menuResetSubscription:unknown; event:Event
 * Outputs: 
 * Validation: 
 * Dependencies: Router, LayoutService, MenuHelpService
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=component/app.menuitem; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Component, HostBinding, Input, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { TOOLTIP_DELAY as GLOBAL_TOOLTIP_DELAY } from '../../constant/app.constant';
import { PopoverModule } from 'primeng/popover';
import { Popover } from 'primeng/popover';
import { MenuHelpService } from '../../services/menu-help.service';
import { MENU, type MenuKey } from './menu.registry';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../service/layout.service';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { GenusMediaAccessService, GenusMediaAccessLevel } from './genus-media-access.service';
import { BetaAccessPinDialogComponent } from './beta-access-pin-dialog.component';

@Component({
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, RippleModule, TooltipModule, PopoverModule, DynamicDialogModule],
    styles: [`
        .menu-root-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
        }
        .menu-root-header .root-header-left {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }
        .menu-root-header .root-header-right {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .menu-lock-button {
            border: none;
            background: transparent;
            color: inherit;
            cursor: pointer;
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.95rem;
        }
        .menu-lock-button:hover,
        .menu-lock-button:focus {
            color: var(--primary-color, #3b82f6);
        }
    `],
    template: `
        <ng-container>
            <div
                *ngIf="root && item.visible !== false"
                class="layout-menuitem-root-text menu-root-header"
                (click)="toggleSection($event)"
                [pTooltip]="categoryTooltip"
                appendTo="body"
                tooltipPosition="right"
                [showDelay]="TOOLTIP_DELAY"
            >
                <div class="root-header-left">
                    <i *ngIf="item.icon" [ngClass]="item.icon" class="layout-menuitem-icon clickable-help" (click)="$event.stopPropagation(); openCategoryHelp($event, op)"></i>
                    <span>{{ item.label }}</span>
                </div>
                <div class="root-header-right">
                    <button
                        *ngIf="isGenusMediaBucket()"
                        type="button"
                        class="menu-lock-button"
                        (click)="onLockClick($event)"
                        [pTooltip]="getGenusMediaLockTooltip()"
                        appendTo="body"
                        tooltipPosition="left"
                        [showDelay]="TOOLTIP_DELAY"
                        aria-label="Manage Genus Media access"
                    >
                        <i class="pi" [ngClass]="getGenusMediaLockIcon()"></i>
                    </button>
                    <i class="pi pi-fw" [ngClass]="{'pi-chevron-down': active, 'pi-chevron-right': !active}"></i>
                </div>
            </div>
            <a *ngIf="(!item.routerLink || item.items) && item.visible !== false && !root" [attr.href]="item.url" (click)="itemClick($event)" [ngClass]="item.styleClass" target="_self" tabindex="0" pRipple [pTooltip]="itemTooltip" appendTo="body" tooltipPosition="right" [showDelay]="TOOLTIP_DELAY">
                <i [ngClass]="item.icon" class="layout-menuitem-icon clickable-help" (click)="$event.preventDefault(); $event.stopPropagation(); openItemHelp($event, op)"></i>
                <span class="layout-menuitem-text">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
            </a>
            <a
                *ngIf="item.routerLink && !item.items && item.visible !== false"
                (click)="itemClick($event)"
                [ngClass]="item.styleClass"
                [routerLink]="item.routerLink"
                routerLinkActive="active-route"
                [routerLinkActiveOptions]="item.routerLinkActiveOptions || { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }"
                [fragment]="item.fragment"
                [queryParamsHandling]="item.queryParamsHandling"
                [pTooltip]="itemTooltip"
                appendTo="body"
                tooltipPosition="right"
                [showDelay]="TOOLTIP_DELAY"
                [preserveFragment]="item.preserveFragment"
                [skipLocationChange]="item.skipLocationChange"
                [replaceUrl]="item.replaceUrl"
                [state]="item.state"
                [queryParams]="item.queryParams"
                target="_self"
                tabindex="0"
                pRipple
            >
                <i [ngClass]="item.icon" class="layout-menuitem-icon clickable-help" (click)="$event.preventDefault(); $event.stopPropagation(); openItemHelp($event, op)"></i>
                <span class="layout-menuitem-text">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
            </a>

            <ul *ngIf="(item.items && item.visible !== false)" [@children]="root ? (active ? 'visible' : 'hidden') : active ? 'visible' : 'hidden'">
                <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
                    <li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child['badgeClass']"></li>
                </ng-template>
            </ul>
            <p-popover #op appendTo="body" [style]="{width:'420px','max-width':'min(92vw,480px)'}">
                <div style="white-space: pre-wrap; line-height:1.35">{{ helpMd }}</div>
            </p-popover>
        </ng-container>
    `,
    animations: [
        trigger('children', [
            state('hidden', style({
                height: '0',
                overflow: 'hidden',
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', [
                style({ overflow: 'hidden' }),
                animate('300ms ease-in-out')
            ])
        ])
    ],
    providers: [DialogService]
})
export class AppMenuitem implements OnInit {
    @Input() item!: MenuItem;

    @Input() index!: number;

    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

    @Input() parentKey!: string;

    active = false;

    menuSourceSubscription: Subscription;

    menuResetSubscription: Subscription;

    key: string = '';
    // Use global tooltip delay for consistency across the portal
    TOOLTIP_DELAY = GLOBAL_TOOLTIP_DELAY;

    categoryTooltip: string = '';
    itemTooltip: string = '';
    helpMd: string = '';

    private readonly genusMediaAccess = inject(GenusMediaAccessService);
    private readonly dialogService = inject(DialogService);

    constructor(
        public router: Router,
        public layoutService: LayoutService,
        private helpService: MenuHelpService
    ) {
        this.menuSourceSubscription = this.layoutService.menuSource$.subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
            });
        });

        this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            if (this.item.routerLink) {
                this.updateActiveStateFromRoute();
            }
        });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        if (this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }
        
        // Default root menu sections based on builder-provided flag; fallback to expanded
        if (this.root) {
            const expandedByDefault = (this.item as any)['expandedByDefault'];
            this.active = (typeof expandedByDefault === 'boolean') ? expandedByDefault : true;
            // derive bucket from label (SETTING, CONTENT, ...)
            const bucket = String(this.item.label || '').toUpperCase();
            this.helpService.getCategoryTooltip(bucket).subscribe(t => this.categoryTooltip = t || '');
        }
        // For leaf items, compute key from route mapping to MENU
        if (!this.root && !this.item.items) {
            const route = Array.isArray(this.item.routerLink) ? String(this.item.routerLink[0]) : '';
            const key = this.findMenuKeyByRoute(route);
            if (key) {
                this.helpService.getItemTooltip(key).subscribe(t => this.itemTooltip = t || '');
            }
        }
    }

    updateActiveStateFromRoute() {
        let activeRoute = this.router.isActive(this.item.routerLink[0], { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' });

        if (activeRoute) {
            this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    async toggleSection(event: Event) {
        event.preventDefault();
        
        if (this.root) {
            if (this.isGenusMediaBucket() && !this.active && !this.genusMediaAccess.hasUnlockedAccess()) {
                const level = await this.promptForGenusMediaAccess();
                if (!level) {
                    return;
                }
                this.active = true;
                this.layoutService.onMenuStateChange({ key: this.key });
                return;
            }
            this.active = !this.active;
        }
    }

    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // Prevent default behavior for navigation items to ensure proper routing
        if (this.item.routerLink && !this.item.items) {
            // Let Angular router handle the navigation
            event.preventDefault();
            this.router.navigate(this.item.routerLink);
            return;
        }

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;
        }

        this.layoutService.onMenuStateChange({ key: this.key });
    }

    openCategoryHelp(event: Event, op: Popover) {
        const bucket = String(this.item.label || '').toUpperCase();
        this.helpService.getMarkdownForCategory(bucket).subscribe(url => {
            if (!url) { this.helpMd = this.categoryTooltip || 'No help available.'; op.toggle(event); return; }
            this.helpService.loadMarkdown(url).subscribe(md => { this.helpMd = md || this.categoryTooltip || 'No help available.'; op.toggle(event); });
        });
    }

    openItemHelp(event: Event, op: Popover) {
        const route = Array.isArray(this.item.routerLink) ? String(this.item.routerLink[0]) : '';
        const key = this.findMenuKeyByRoute(route);
        if (!key) { this.helpMd = this.itemTooltip || 'No help available.'; op.toggle(event); return; }
        this.helpService.getMarkdownForItem(key).subscribe(url => {
            if (!url) { this.helpMd = this.itemTooltip || 'No help available.'; op.toggle(event); return; }
            this.helpService.loadMarkdown(url).subscribe(md => { this.helpMd = md || this.itemTooltip || 'No help available.'; op.toggle(event); });
        });
    }

    private findMenuKeyByRoute(route: string): MenuKey | '' {
        if (!route) return '' as any;
        const entries = Object.entries(MENU) as [MenuKey, { route: string }][];
        const found = entries.find(([, v]) => v.route === route);
        return found ? found[0] : ('' as any);
    }

    @HostBinding('class.active-menuitem')
    get activeClass() {
        return this.active && !this.root;
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }

    isGenusMediaBucket(): boolean {
        return this.root && String(this.item.label || '').toUpperCase() === 'GENUSMEDIA';
    }

    private async promptForGenusMediaAccess(): Promise<GenusMediaAccessLevel | null> {
        const dialogRef = this.dialogService.open(BetaAccessPinDialogComponent, {
            header: 'Beta Access Required',
            width: '360px',
            closable: false,
            dismissableMask: false,
            data: {}
        });
        const result = await firstValueFrom(dialogRef.onClose);
        if (result === 'creator' || result === 'full') {
            return result;
        }
        return null;
    }

    async onLockClick(event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        const level = await this.promptForGenusMediaAccess();
        if (level) {
            this.active = true;
            this.layoutService.onMenuStateChange({ key: this.key });
        }
    }

    getGenusMediaLockIcon(): string {
        const level = this.genusMediaAccess.currentLevel();
        if (level === 'full') {
            return 'pi-unlock';
        }
        if (level === 'creator') {
            return 'pi-lock-open';
        }
        return 'pi-lock';
    }

    getGenusMediaLockTooltip(): string {
        const level = this.genusMediaAccess.currentLevel();
        if (level === 'full') {
            return 'Full access granted. Click to change PIN access.';
        }
        if (level === 'creator') {
            return 'Creator access granted. Click to unlock full beta access.';
        }
        return 'Beta access locked. Click to enter PIN.';
    }
}
